import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Popconfirm,
  Space,
  Table,
  Tag,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import { api, type Sensor } from "../api";
import SensorForm from "../components/SensorForm";
import { useRole } from "../contexts/AuthContext";

export default function SFlowSources() {
  const { isAdmin, isOperator } = useRole();
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingID, setEditingID] = useState<number | undefined>(undefined);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.sensors();
      setSensors(s);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function onDelete(id: number) {
    try {
      await api.deleteSensor(id);
      message.success("Sensor deleted");
      reload();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  }

  const columns: ColumnsType<Sensor> = [
    { title: "ID", dataIndex: "id", width: 60 },
    {
      title: "Name",
      dataIndex: "name",
      render: (v: string, row) => <Link to={`/sensors/${row.id}`}>{v}</Link>,
    },
    {
      title: "Protocol",
      dataIndex: "protocol",
      width: 100,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    { title: "Listen port", dataIndex: "listen_port", width: 110 },
    {
      title: "Zones",
      width: 120,
      render: (_: unknown, row: Sensor) => {
        const ids =
          row.zone_ids && row.zone_ids.length > 0
            ? row.zone_ids
            : row.zone_id
              ? [row.zone_id]
              : [];
        if (ids.length === 0) return <span style={{ color: "#888" }}>—</span>;
        return (
          <Space size={4} wrap>
            {ids.map((z, i) => (
              <Tag key={z} color={i === 0 ? "blue" : undefined}>
                {z}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      width: 90,
      render: (v: boolean) =>
        v ? <Badge status="success" text="on" /> : <Badge status="default" text="off" />,
    },
    ...(isOperator ? [{
      title: "",
      width: 100,
      render: (_: unknown, row: Sensor) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => { setEditingID(row.id); setFormOpen(true); }}
          />
          {isAdmin && (
            <Popconfirm title={`Delete sensor "${row.name}"?`} onConfirm={() => onDelete(row.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ];

  return (
    <Card
      title="sFlow Sources"
      extra={
        isOperator && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => { setEditingID(undefined); setFormOpen(true); }}
          >
            Add sFlow source
          </Button>
        )
      }
    >
      <Table
        rowKey="id"
        loading={loading}
        dataSource={sensors}
        columns={columns}
        pagination={false}
        size="middle"
        locale={{ emptyText: "No sensors yet. Click 'Add sFlow source' to configure your first exporter." }}
      />
      <SensorForm
        open={formOpen}
        sensorId={editingID}
        onClose={() => setFormOpen(false)}
        onSaved={reload}
      />
    </Card>
  );
}
